import React, { useEffect, useState, useCallback, useMemo } from "react";
import { getRequest, postRequest, putRequest, deleteRequest } from "../../apiWrapper";
import { toast } from "react-toastify";
import {
  FaTag, FaUserShield, FaCalendarAlt, FaHistory, FaTrophy,
  FaGraduationCap, FaChartLine, FaPlus, FaSave, FaUserCircle, FaSearch, FaCheckCircle, FaEdit, FaTrashAlt, FaTimesCircle
} from "react-icons/fa";
import { formatDate, formatDateTimeLocal } from "../../utils/formatDate";

const StudentKarte = () => {
  const role = sessionStorage.getItem("userRole");
  const isAdmin = role === "admin";
  const myTeacherId = sessionStorage.getItem("teacherid");

  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [testDefinitions, setTestDefinitions] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const [profile, setProfile] = useState({ teaching_policy: "" });
  const [records, setRecords] = useState([]);
  const [handovers, setHandovers] = useState([]);
  const [tags, setTags] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [isTagModalOpen, setIsTagModalOpen] = useState(false);
  const [newTagName, setNewTagName] = useState(""); // 新規タグ作成用

  const [newRecord, setNewRecord] = useState("");
  const [handoverForm, setHandoverForm] = useState({ subjectid: "", teacherid: myTeacherId || "", content: "" });

  // 成績入力用
  const [selectedCategory, setSelectedCategory] = useState("");
  const [testForm, setTestForm] = useState({ test_definition_id: "" });
  const [inputScores, setInputScores] = useState({}); // { subjectId: score }
  const [inputAverages, setInputAverages] = useState({}); // { subjectId: average_score }
  // 編集モーダルの状態管理用
  const [editModal, setEditModal] = useState({ open: false, type: "", data: null });
  const [targetSchools, setTargetSchools] = useState([]);
  // 入力中の値を一時的に保持するためのState（オブジェクト形式）
  const [targetInput, setTargetInput] = useState({ 1: "", 2: "", 3: "" });

  const [lessons, setLessons] = useState([]);
  const [lessonForm, setLessonForm] = useState({ lesson_name: "", day_of_week: 1, remarks: "" });

  // --- 通知用のステート ---
  const [notifications, setNotifications] = useState([]);

  // --- 通知の取得処理（生徒未選択時に取得） ---
  useEffect(() => {
    if (!selectedId) {
      getRequest("student_karte_notifications").then(res => {
        if (res.notifications) setNotifications(res.notifications);
      });
    }
  }, [selectedId]);


  // 1. 初期化データ取得
  useEffect(() => {
    const init = async () => {
      try {
        const [stRes, subRes, tdRes] = await Promise.all([
          getRequest("students?excludeWithdrawn=true"),
          getRequest("subjects"),
          getRequest("test_definitions"),
        ]);
        setStudents(stRes.students || []);
        setSubjects(subRes.subjects || []);

        const defs = tdRes.testDefinitions || [];
        const formattedDefs = defs.map(d => ({
          ...d,
          displayName: `${d.academic_year}年度 第${d.term}回 ${d.name}`
        }));
        setTestDefinitions(formattedDefs);

        // 最新の試験をデフォルト選択（defs[0] が最新と想定）
        if (formattedDefs.length > 0) {
          setTestForm({ test_definition_id: formattedDefs[0].id });
        }

        if (isAdmin) {
          const tRes = await getRequest("teachers?excludeRetired=true");
          setTeachers(tRes.teachers || []);
        }
        const params = new URLSearchParams(window.location.search);
        if (params.get("studentid")) setSelectedId(params.get("studentid"));
      } catch (e) {
        toast.error("データの取得に失敗しました");
        console.log(e);
      }
    };
    init();
  }, [isAdmin]);

  // 2. 生徒データ取得
  const fetchData = useCallback(async (id) => {
    if (!id) return;

    setTargetSchools([]);
    setLessons([]);
    setTargetInput({ 1: "", 2: "", 3: "" });

    const safeGet = async (u) => (await getRequest(u).catch(() => ({})));
    const [prof, rec, hand, tag, targetRes, lessonRes] = await Promise.all([
      safeGet(`student_profiles/${id}`),
      safeGet(`student_records?studentid=${id}`),
      safeGet(`student_handovers?studentid=${id}`),
      safeGet(`student_tags?studentid=${id}`),
      safeGet(`student_target_schools?studentid=${id}`),
      safeGet(`student_lessons?studentid=${id}`),
    ]);
    setProfile(prof.profile || { teaching_policy: "" });
    setRecords(rec.records || []);
    setHandovers(hand.handovers || []);
    setTags(tag.tags || []);
    // 取得した志望校データを入力用Stateにマッピング
    const newTargets = { 1: "", 2: "", 3: "" };
    if (targetRes?.targets) {
      targetRes.targets.forEach(t => {
        newTargets[t.priority] = t.school_name;
      });
    }
    setTargetInput(newTargets);
    setTargetSchools(targetRes.targets || []);
    setLessons(lessonRes.lessons || []);
  }, []);

  useEffect(() => {
    if (selectedId) fetchData(selectedId);
  }, [selectedId, fetchData]);

  // --- 追加：特定の試験の成績を読み込む関数 ---
  const fetchTestScores = useCallback(async (testDefId) => {
    if (!selectedId || !testDefId) return;
    try {
      const res = await getRequest(`student_test_results/${selectedId}/${testDefId}`);
      const scores = {};
      const averages = {};

      if (res.results && res.results.length > 0) {
        res.results.forEach(item => {
          scores[item.test_subject_id] = item.score;
          averages[item.test_subject_id] = item.average;
        });
      }
      setInputScores(scores);
      setInputAverages(averages);
    } catch (e) {
      console.error("成績取得失敗", e);
    }
  }, [selectedId]);

  // 試験名が変更されたら成績を読み込む
  useEffect(() => {
    if (testForm.test_definition_id) {
      fetchTestScores(testForm.test_definition_id);
    } else {
      setInputScores({});
      setInputAverages({});
    }
  }, [testForm.test_definition_id, fetchTestScores]);

  // 生徒が切り替わった際も、現在選択中の試験があれば読み込む
  useEffect(() => {
    if (selectedId && testForm.test_definition_id) {
      fetchTestScores(testForm.test_definition_id);
    }
  }, [selectedId, testForm.test_definition_id, fetchTestScores]);

  // マスタータグ一覧を取得
  const fetchAllTags = useCallback(async () => {
    try {
      const res = await getRequest("tags");
      if (res?.tags) setAllTags(res.tags);
    } catch (e) { console.error(e); }
  }, []);

  // 初回およびタグ更新時にマスターを再取得
  useEffect(() => {
    fetchAllTags();
  }, [fetchAllTags]);

  // 保存処理
  const handleSaveEdit = async () => {
    const { type, data } = editModal;
    try {
      const endpoint = type === "record" ? `student_records/${data.id}` : `student_handovers/${data.id}`;
      // 引継ぎの場合は科目と講師も送る
      const body = type === "record" ? { content: data.content } : { content: data.content, subjectid: data.subjectid, teacherid: data.teacherid };

      await putRequest(endpoint, body);
      toast.success("更新しました");
      setEditModal({ open: false, type: "", data: null });
      fetchData(selectedId);
    } catch (e) { toast.error("更新に失敗しました"); }
  };

  // 削除処理
  const handleDelete = async (type, id) => {
    if (!window.confirm("この項目を削除してもよろしいですか？")) return;
    try {
      const endpoint = type === "record" ? `student_records/${id}` : `student_handovers/${id}`;
      const res = await deleteRequest(endpoint);
      if (res.ok || res.success) {
        toast.success("削除しました");
        fetchData(selectedId);
      }
    } catch (e) { toast.error("削除に失敗しました"); }
  };

  // 成績保存
  const handleSaveScores = async () => {
    if (!testForm.test_definition_id) return toast.warn("試験を選択してください");

    const allSubjectIds = Array.from(new Set([
      ...Object.keys(inputScores),
      ...Object.keys(inputAverages)
    ]));

    let validationError = null; // エラーメッセージ格納用

    const scoresArray = allSubjectIds
      .map(subId => {
        // 既にエラーが見つかっている場合はスキップ
        if (validationError) return null;

        const scoreVal = inputScores[subId];
        const avgVal = inputAverages[subId];

        const hasScore = scoreVal !== "" && scoreVal !== null && scoreVal !== undefined;
        const hasAvg = avgVal !== "" && avgVal !== null && avgVal !== undefined;

        // 【バリデーション】平均があるのに点数がない場合
        if (hasAvg && !hasScore) {
          const subjectName = subjects.find(s => s.id === subId)?.name || "対象科目";
          validationError = `${subjectName}の点数を入力してください（平均点のみの登録はできません）`;
          return null;
        }

        // 両方未入力は除外対象
        if (!hasScore && !hasAvg) return null;

        return {
          subjectid: subId,
          score: hasScore ? parseInt(scoreVal) : 0,
          average_score: hasAvg ? parseInt(avgVal) : 0
        };
      })
      .filter(item => item !== null);

    // ★ バリデーションエラーがあればトーストを表示してここで完全に中断
    if (validationError) {
      return toast.warn(validationError);
    }

    // 登録対象が一つもない場合
    if (scoresArray.length === 0) {
      return toast.warn("登録するスコアが入力されていません");
    }

    try {
      await postRequest("student_test_results", {
        studentid: selectedId,
        test_definition_id: testForm.test_definition_id,
        scores: scoresArray
      });

      toast.success("成績を保存・更新しました！");
      fetchData(selectedId);

      await addNotification('score', '成績・テスト結果を更新しました');
    } catch (e) {
      toast.error("保存に失敗しました");
    }
  };

  // StudentKarte.js 内の handleAddHandover を修正

  const handleAddHandover = async () => {
    if (!handoverForm.content || !handoverForm.subjectid) return toast.warn("科目と内容を入力");
    try {
      // サーバー側の修正に合わせて、teacheridは自動またはbody優先
      await postRequest("student_handovers", { ...handoverForm, studentid: selectedId });
      toast.success("引継ぎ事項を投稿しました");
      setHandoverForm({ ...handoverForm, content: "" });
      fetchData(selectedId);

      await addNotification('handover', '講師連携ノートを投稿しました');
    } catch (e) {
      toast.error("投稿に失敗しました");
    }
  };

  const handleAddRecord = async () => {
    if (!newRecord) return;
    try {
      await postRequest("student_records", { studentid: selectedId, content: newRecord });
      toast.success("記録を保存しました"); // ついでにトーストを出すと親切です
      setNewRecord("");
      fetchData(selectedId); // ★ データを再取得して表示を更新

      await addNotification('record', `備忘録を追加しました: ${newRecord.substring(0, 15)}...`);
    } catch (e) {
      toast.error("記録の保存に失敗");
    }
  };

  // タグの関連付け（追加）
  const handleAddTag = async (tagId) => {
    try {
      await postRequest("student_tags", { studentid: selectedId, tag_id: tagId });
      fetchData(selectedId); // リスト更新
    } catch (e) {
      toast.error("タグの追加に失敗しました");
    }
  };

  // タグの追加（名前で送信）
  const handleAddTagByName = async (tagName) => {
    if (!tagName.trim()) return;
    try {
      // APIが name を受け取って内部でID解決・作成をする仕様に合わせる
      await postRequest("student_tags", {
        studentid: selectedId,
        name: tagName.trim()
      });
      setNewTagName("");
      fetchData(selectedId); // 最新のタグ一覧を再取得
    } catch (e) {
      toast.error("タグの追加に失敗しました");
    }
  };

  const handleToggleTag = async (tag) => {
    const isAttached = tags.some(t => t.id === tag.id);

    try {
      if (isAttached) {
        // 1. パスを確認: student_tags に対して、クエリでIDを2つ渡す
        const response = await deleteRequest(`student_tags?studentid=${selectedId}&tag_id=${tag.id}`);

        if (response.ok) {
          fetchData(selectedId); // 成功時のみ再読込
          await addNotification('tag', `タグ「${tag.name}」を外しました`);
        } else {
          toast.error("タグの解除に失敗しました");
        }
      } else {
        // 追加（POST）
        const response = await postRequest("student_tags", {
          studentid: selectedId,
          name: tag.name
        });
        // postRequestがresponse.okを返す設計なら同様に判定
        fetchData(selectedId);
        await addNotification('tag', `タグ「${tag.name}」を追加しました`);
      }

      if (typeof fetchAllTags === 'function') {
        await fetchAllTags();
      }
    } catch (e) {
      console.error("❌ タグ操作エラー:", e);
      toast.error("通信に失敗しました");
    }
  };

  // 志望校の保存（Priority別）
  const handleSaveTargetSchool = async (priority, schoolName) => {
    try {
      const oldTarget = targetSchools.find(t => t.priority === priority);
      const oldSchoolName = oldTarget?.school_name || "未設定";

      // 2. 新しい値（引数で渡された schoolName）
      const newSchoolName = schoolName || "未設定";

      console.log(`比較: 旧「${oldSchoolName}」 -> 新「${newSchoolName}」`);

      // 変更がない場合は、API通信も通知もスキップ
      if (oldSchoolName === newSchoolName) {
        console.log("変更がないため終了します");
        return;
      }

      await postRequest("student_target_schools", {
        studentid: selectedId,
        school_name: schoolName,
        priority: priority
      });
      toast.success(`第${priority}志望を更新しました`);

      // 「保存済み」の状態にする
      setTargetSchools(prev => {
        const otherSchools = prev.filter(s => s.priority !== priority);
        const updatedList = [...otherSchools, { priority, school_name: schoolName }];
        // 優先順位順に並び替えて保持
        return updatedList.sort((a, b) => a.priority - b.priority);
      });

      await addNotification(
        'target',
        `第${priority}志望を「${oldSchoolName}」から「${newSchoolName}」に変更しました`
      );
    } catch (e) { toast.error("志望校の保存に失敗しました"); }
  };

  // 習い事の追加
  const handleAddLesson = async () => {
    if (!lessonForm.lesson_name) return toast.warn("習い事名を入力してください");
    try {
      await postRequest("student_lessons", {
        studentid: selectedId,
        ...lessonForm
      });
      setLessonForm({ lesson_name: "", day_of_week: 1, remarks: "" });
      fetchData(selectedId);
      toast.success("習い事を追加しました");

      await addNotification('lesson', '習い事を追加しました');
    } catch (e) { toast.error("追加に失敗しました"); }
  };

  // 習い事の削除
  const handleDeleteLesson = async (id) => {
    if (!window.confirm("この習い事を削除しますか？")) return;
    try {
      await deleteRequest(`student_lessons/${id}`);
      fetchData(selectedId);
      toast.success("削除しました");
    } catch (e) { toast.error("削除に失敗しました"); }
  };

  const filteredStudents = useMemo(() =>
    students.filter(s => s.name?.includes(searchTerm) || s.kana?.includes(searchTerm)),
    [students, searchTerm]);

  const categories = useMemo(() => Array.from(new Set(subjects.map(s => s.category).filter(Boolean))), [subjects]);
  const filteredSubjects = useMemo(() => subjects.filter(s => s.category === selectedCategory), [subjects, selectedCategory]);

  // URLのクエリパラメータを書き換えるヘルパー
  const setQuery = (params) => {
    const url = new URL(window.location.href);
    Object.entries(params).forEach(([k, v]) => {
      if (v === undefined || v === null || v === "") url.searchParams.delete(k);
      else url.searchParams.set(k, v);
    });
    window.history.replaceState({}, "", url.toString());
  };

  // 生徒選択時の処理（状態更新 + URL更新）
  const handleStudentChange = (id) => {
    setSelectedId(id);
    setQuery({ studentid: id });
  };

  // --- 追加：試験リストの再取得・フォーマット用ヘルパー ---
  const refreshTestDefinitions = async () => {
    try {
      const tdRes = await getRequest("test_definitions");
      const defs = tdRes.testDefinitions || [];
      const formattedDefs = defs.map(d => ({
        ...d,
        displayName: `${d.academic_year}年度 第${d.term}回 ${d.name}`
      }));
      setTestDefinitions(formattedDefs);
    } catch (e) {
      console.error("試験リストの更新に失敗しました", e);
    }
  };

  // --- タグマスタの削除 (関連データ含め) ---
  const handleDeleteMasterTag = async (tagId) => {
    if (!window.confirm("このタグをマスタから完全に削除しますか？\n全生徒に設定されているこのタグもすべて削除されます。")) return;
    try {
      // 1. サーバー側で「student_tags」のリレーションも削除するようにAPIを実行
      // ※API側がマスタ削除＝リレーション削除を兼ねている前提
      await deleteRequest(`tags/${tagId}`);
      toast.success("タグを完全に削除しました");

      fetchAllTags(); // マスタ一覧を再取得
      if (selectedId) fetchData(selectedId); // 現在表示中の生徒のタグ表示を更新
    } catch (e) {
      toast.error("タグの削除に失敗しました");
    }
  };

  // --- 試験の新規登録 ---
  const handleAddTestDefinition = async () => {
    const name = window.prompt("試験名を入力（例：中間テスト）");
    const term = window.prompt("回数を入力（例：1）");
    const academic_year = new Date().getFullYear();

    if (!name || !term) return;
    try {
      await postRequest("test_definitions", { name, term, academic_year });
      toast.success("試験を登録しました");
      await refreshTestDefinitions(); // フォーマットを適用して再取得
    } catch (e) {
      toast.error("登録に失敗しました");
    }
  };

  // --- 試験の削除 (関連データ含め) ---
  const handleDeleteTestDefinition = async (id) => {
    // 1. 対象の試験情報を取得
    const targetTest = testDefinitions.find(d => d.id === id);
    if (!targetTest) return;

    const testName = targetTest.displayName;

    // 2. 最初の確認
    if (!window.confirm(`【警告】試験「${testName}」を削除しますか？\nこの試験に紐づく全生徒の成績データもすべて削除され、復元できません。`)) {
      return;
    }

    // 3. 試験名の入力による最終確認
    const userInput = window.prompt(
      `最終確認です。本当に削除する場合は、確認のために試験名を入力してください。\n入力内容： ${testName}`
    );

    if (userInput !== testName) {
      toast.error("入力内容が一致しないため、削除をキャンセルしました。");
      return;
    }

    // 4. 実行
    try {
      await deleteRequest(`test_definitions/${id}`);
      toast.success("試験と関連データを完全に削除しました");

      await refreshTestDefinitions(); // 試験リストを再取得・フォーマット
      setTestForm({ test_definition_id: "" }); // 選択をクリア
    } catch (e) {
      toast.error("試験の削除に失敗しました");
    }
  };

  // --- 通知送信用ヘルパー関数 ---
  const addNotification = async (type, message) => {
    if (!selectedId) return;
    try {
      await postRequest("student_karte_notifications", {
        studentid: selectedId,
        action_type: type,
        content: message
      });
    } catch (e) {
      console.error("通知の送信に失敗しました", e);
    }
  };

  // 学年数値 (1〜12) または文字列を表記用（小1〜高3）に変換する関数
  const formatShortGrade = (grade) => {
    if (!grade) return "";
    const num = Number(grade);
    if (isNaN(num)) return grade; // すでに「中2」などの文字列の場合はそのまま返す
    if (num >= 1 && num <= 6) return `小${num}`;
    if (num >= 7 && num <= 9) return `中${num - 6}`;
    if (num >= 10 && num <= 12) return `高${num - 9}`;
    return "";
  };

  // 選択されている生徒のオブジェクトを取得
  const selectedStudent = useMemo(() => {
    if (!selectedId || !students) return null;
    return students.find((s) => String(s.id || s.studentid) === String(selectedId));
  }, [students, selectedId]);

  return (
    <div className="pt-20 max-w-6xl mx-auto p-4 space-y-6 bg-gray-50 min-h-screen pb-20">
      {/* 生徒選択 */}
      <div className="bg-white p-4 rounded-xl shadow-sm border border-blue-100 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2 text-blue-600 font-bold"><FaUserCircle size={24} /> <span>生徒選択:</span></div>
        <div className="relative flex-1 min-w-[200px]">
          <FaSearch className="absolute left-3 top-3 text-gray-400" />
          <input type="text" placeholder="名前で検索..." className="w-full pl-10 pr-4 py-2 border rounded-lg" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
        </div>
        <select className="flex-1 min-w-[200px] p-2 border rounded-lg" value={selectedId} onChange={e => handleStudentChange(e.target.value)}>
          <option value="">-- 生徒を選択 --</option>
          {filteredStudents.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </div>

      {!selectedId ? (
        isAdmin && (
          <div className="bg-white rounded-xl shadow-sm border p-6 animate-fadeIn mt-6">
            <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
              <FaHistory className="mr-2 text-blue-500" /> 最近のアクティビティ
            </h2>
            <div className="space-y-4">
              {notifications.length > 0 ? notifications.map(n => (
                <div key={n.id} className="flex items-start gap-4 p-3 hover:bg-gray-50 rounded-lg transition-colors border border-gray-100">
                  <div className="flex-1">
                    <div className="flex justify-between items-center mb-1">
                      <span className="font-bold text-sm text-gray-700">{n.student_name} さん</span>
                      <span className="text-[10px] text-gray-400">{formatDateTimeLocal(n.created_at)}</span>
                    </div>
                    <p className="text-xs text-gray-600 font-medium">{n.content}</p>
                    <p className="text-[10px] text-blue-500 mt-1">更新者：{n.teacher_name}</p>
                  </div>
                  <button
                    onClick={() => handleStudentChange(n.studentid)}
                    className="text-xs bg-white border border-gray-200 px-3 py-1.5 rounded hover:bg-gray-100 font-bold text-gray-600"
                  >
                    確認
                  </button>
                </div>
              )) : (
                <p className="text-sm text-gray-400 text-center py-6">最近の更新はありません</p>
              )}
            </div>
          </div>
        )
      ) : (
        <div className="animate-fadeIn space-y-6">
          {/* 既存のカルテUI */}
          {/* 生徒が選択されている場合のメイン表示エリア上部 */}
          {selectedStudent && (
            <div className="flex items-center gap-3 mb-6 pb-3 border-b border-gray-200">
              <h1 className="text-2xl font-bold text-gray-800">
                {selectedStudent.name || "名前不明"}
              </h1>
              {selectedStudent.grade && (
                <span className="bg-blue-100 text-blue-800 text-sm font-semibold px-3 py-1 rounded-full">
                  {formatShortGrade(selectedStudent.grade)}
                </span>
              )}
            </div>
          )}

          {/* 修正箇所1：トップのレイアウト（左：方針、右：タグ＆志望校） */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

            {/* 左カラム: 指導方針 */}
            <div className="bg-white p-5 rounded-xl shadow-sm border-t-4 border-orange-400 flex flex-col">
              <h3 className="font-bold text-gray-700 mb-4 flex items-center">
                <FaUserShield className="mr-2 text-orange-400" /> 指導方針
              </h3>

              <textarea
                className={`w-full border rounded-lg p-3 text-sm min-h-[200px] flex-1 ${!isAdmin ? 'bg-gray-100 text-gray-600' : ''}`}
                value={profile.teaching_policy || ""}
                onChange={e => setProfile({ ...profile, teaching_policy: e.target.value })}
                readOnly={!isAdmin}
                placeholder={isAdmin ? "指導方針を入力してください..." : "指導方針が設定されていません"}
              />

              {isAdmin && (
                <button
                  onClick={() =>
                    putRequest(`student_profiles/${selectedId}`, { teaching_policy: profile.teaching_policy })
                      .then(() => toast.success("指導方針を保存しました"))
                      .catch(() => toast.error("保存に失敗しました"))
                  }
                  className="mt-2 w-full bg-orange-400 text-white py-2 rounded font-bold hover:bg-orange-500 transition-colors"
                >
                  保存
                </button>
              )}
            </div>

            {/* 右カラム: タグ（上）と志望校（下）を縦にまとめる */}
            <div className="flex flex-col gap-6">

              {/* 生徒タグセクション */}
              <div className="bg-white p-5 rounded-xl shadow-sm border-t-4 border-blue-400">
                <h3 className="font-bold text-gray-700 mb-4 flex items-center">
                  <FaTag className="mr-2 text-blue-400" /> 生徒タグ
                </h3>

                <div className="flex flex-wrap gap-2 mb-6">
                  {tags.length > 0 ? (
                    tags.map(tag => (
                      <span key={tag.id} className="bg-blue-600 text-white px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                        {tag.name}
                        <button onClick={() => handleToggleTag(tag)} className="ml-1 hover:text-red-200">✕</button>
                        {isAdmin && (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDeleteMasterTag(tag.id); }}
                            className="ml-1 hover:text-red-500"
                          >
                            <FaTimesCircle size={10} />
                          </button>
                        )}
                      </span>
                    ))
                  ) : (
                    <span className="text-gray-400 text-xs italic">タグが設定されていません</span>
                  )}
                </div>

                <div className="border-t pt-4">
                  <p className="text-[10px] font-bold text-gray-500 mb-2 uppercase">タグマスタから追加 / 新規作成</p>
                  <div className="flex gap-2 mb-3">
                    <input
                      className="flex-1 border rounded-lg px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-blue-300"
                      placeholder="新しいタグ名..."
                      value={newTagName}
                      onChange={e => setNewTagName(e.target.value)}
                    />
                    <button
                      onClick={() => {
                        handleAddTagByName(newTagName);
                        fetchAllTags();
                      }}
                      className="bg-blue-500 text-white px-4 py-1.5 rounded-lg text-xs font-bold"
                    >
                      新規追加
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-1.5 overflow-y-auto max-h-32">
                    {allTags.map(tag => {
                      const isAttached = tags.some(t => t.id === tag.id);
                      if (isAttached) return null;
                      return (
                        <button
                          key={tag.id}
                          onClick={() => handleToggleTag(tag)}
                          className="bg-gray-100 text-gray-600 px-2 py-1 rounded text-[11px] hover:bg-blue-100 hover:text-blue-600 transition-colors"
                        >
                          + {tag.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* 志望校セクション */}
              <div className="bg-white p-5 rounded-xl shadow-sm border">
                <h3 className="font-bold text-gray-700 flex items-center mb-4 text-sm">
                  <FaGraduationCap className="mr-2 text-blue-500" /> 志望校
                </h3>
                <div className="space-y-3">
                  {[1, 2, 3].map(priority => (
                    <div key={priority} className="flex gap-2 items-center">
                      <span className="text-[10px] font-bold text-gray-400 w-8 text-center">第{priority}</span>
                      <input
                        type="text"
                        className="flex-1 border-b text-sm focus:border-blue-500 outline-none p-1"
                        // valueにStateを指定することで、生徒切り替え時に強制的に書き換わる
                        value={targetInput[priority] || ""}
                        onChange={(e) => {
                          // 入力中の文字をStateに即時反映
                          setTargetInput({ ...targetInput, [priority]: e.target.value });
                        }}
                        onBlur={(e) => {
                          // フォーカスが外れたとき（またはEnter）にDB保存
                          handleSaveTargetSchool(priority, e.target.value);
                        }}
                        placeholder="学校名を入力"
                      />
                    </div>
                  ))}
                </div>
              </div>

            </div>
          </div>
          {/* トップレイアウトここまで */}

          {/* 以下、カルテ（備忘録） */}
          <div className="bg-white p-5 rounded-xl shadow-sm border">
            <h3 className="font-bold text-gray-700 mb-4 flex items-center"><FaHistory className="mr-2 text-blue-500" /> 備忘録</h3>
            <div className="flex gap-2 mb-4">
              <input
                className="flex-1 border rounded p-2 text-sm"
                placeholder="日々の記録..."
                value={newRecord}
                onChange={e => setNewRecord(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddRecord()}
              />
              <button
                onClick={handleAddRecord}
                className="bg-blue-600 text-white px-4 rounded hover:bg-blue-700 transition-colors"
              >
                追加
              </button>
            </div>
            <div className="space-y-2 max-h-[200px] overflow-y-auto">
              {records.map(r => (
                <div key={r.id} className="p-3 bg-gray-50 border-l-4 border-blue-400 text-sm flex justify-between items-start">
                  <span className="flex-1">{r.content}</span>
                  <div className="flex items-center gap-3">
                    <span className="text-[10px] text-gray-400">
                      {r.created_at ? new Date(r.created_at).toLocaleDateString('ja-JP') : ""}
                    </span>
                    {isAdmin && (
                      <div className="flex gap-2">
                        <button
                          onClick={() => setEditModal({ open: true, type: "record", data: { ...r } })}
                          className="text-blue-500 hover:text-blue-700"
                        >
                          <FaEdit size={14} />
                        </button>
                        <button
                          onClick={() => handleDelete("record", r.id)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <FaTrashAlt size={14} />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* 引継ぎ */}
          <div className="bg-white p-5 rounded-xl shadow-sm border">
            <h3 className="font-bold text-gray-700 mb-4 flex items-center"><FaChartLine className="mr-2 text-purple-500" /> 講師連携ノート</h3>
            <div className="space-y-2 mb-4 bg-purple-50 p-3 rounded">
              <div className="flex gap-2">
                <select className="border rounded p-1 text-xs flex-1" value={handoverForm.subjectid} onChange={e => setHandoverForm({ ...handoverForm, subjectid: e.target.value })}>
                  <option value="">科目選択</option>
                  {subjects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                {isAdmin && (
                  <select className="border rounded p-1 text-xs flex-1" value={handoverForm.teacherid} onChange={e => setHandoverForm({ ...handoverForm, teacherid: e.target.value })}>
                    <option value="">講師選択</option>
                    {teachers.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                )}
              </div>
              <textarea className="w-full border rounded p-2 text-xs" value={handoverForm.content} onChange={e => setHandoverForm({ ...handoverForm, content: e.target.value })} placeholder="申し送り内容" />
              <button onClick={handleAddHandover} className="w-full bg-purple-600 text-white py-1 rounded text-xs">投稿</button>
            </div>
            <div className="space-y-2 max-h-[150px] overflow-y-auto">
              {handovers.map(h => (
                <div key={h.id} className="p-2 border-b text-[11px] flex justify-between items-start">
                  <div className="flex-1">
                    <span className="font-bold text-purple-700">{h.subject_name} ({h.teacher_name})</span>: {h.content}
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-[9px] text-gray-400">
                      {h.created_at ? new Date(h.created_at).toLocaleDateString('ja-JP') : ""}
                    </span>
                    {isAdmin && (
                      <div className="flex gap-2">
                        <button
                          onClick={() => setEditModal({ open: true, type: "handover", data: { ...h } })}
                          className="text-blue-500 hover:text-blue-700"
                        >
                          <FaEdit size={12} />
                        </button>
                        <button
                          onClick={() => handleDelete("handover", h.id)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <FaTrashAlt size={12} />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* 成績入力 */}
          <div className="bg-white p-5 rounded-xl shadow-sm border">
            <h3 className="font-bold text-gray-700 mb-4 flex items-center text-red-600">
              <FaTrophy className="mr-2" /> 成績・テスト結果入力
            </h3>
            {isAdmin && (
              <button onClick={handleAddTestDefinition} className="text-xs bg-red-100 text-red-600 px-2 py-1 rounded hover:bg-red-200">
                試験追加
              </button>
            )}

            <div className="flex gap-2 mb-4 mt-2">
              <select
                className="flex-1 border p-2 rounded text-sm"
                value={testForm.test_definition_id}
                onChange={e => setTestForm({ test_definition_id: e.target.value })}
              >
                <option value="">試験選択</option>
                {testDefinitions.map(d => (
                  <option key={d.id} value={d.id}>{d.displayName}</option>
                ))}
              </select>
              {isAdmin && testForm.test_definition_id && (
                <button
                  onClick={() => handleDeleteTestDefinition(testForm.test_definition_id)}
                  className="text-red-500 p-2 hover:bg-red-50 rounded transition-colors"
                  title="試験定義を削除"
                >
                  <FaTrashAlt size={16} />
                </button>
              )}
            </div>

            <div className="flex flex-wrap gap-2 mb-4">
              {categories.map(cat => (
                <button key={cat} onClick={() => setSelectedCategory(cat)} className={`px-3 py-1 rounded-full text-xs font-bold transition-all ${selectedCategory === cat ? "bg-red-500 text-white shadow-md" : "bg-gray-100 text-gray-500"}`}>{cat}</button>
              ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-6">
              {filteredSubjects.map(sub => (
                <div key={sub.id} className="border rounded-lg p-3 bg-white shadow-sm border-gray-100 hover:border-red-200 transition-colors">
                  <div className="text-xs font-bold text-gray-600 mb-2 border-b pb-1">{sub.name}</div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-[9px] text-gray-400 text-center mb-1">得点</div>
                      <input
                        type="number"
                        className="w-full text-center text-lg font-bold border-b focus:border-red-500 outline-none bg-transparent"
                        placeholder="-"
                        value={inputScores[sub.id] ?? ""}
                        onChange={e => setInputScores({ ...inputScores, [sub.id]: e.target.value })}
                      />
                    </div>
                    <div>
                      <div className="text-[9px] text-gray-400 text-center mb-1">平均</div>
                      <input
                        type="number"
                        className="w-full text-center text-lg font-bold border-b focus:border-blue-400 outline-none bg-transparent"
                        placeholder="-"
                        value={inputAverages[sub.id] ?? ""}
                        onChange={e => setInputAverages({ ...inputAverages, [sub.id]: e.target.value })}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <button onClick={handleSaveScores} className="w-full bg-red-600 text-white py-3 rounded-xl font-bold shadow-lg hover:bg-red-700 active:scale-95 transition flex items-center justify-center gap-2">
              <FaCheckCircle /> 保存して更新
            </button>
          </div>

          {/* 習い事セクション */}
          <div className="bg-white p-5 rounded-xl shadow-sm border mb-6">
            <h3 className="font-bold text-gray-700 flex items-center mb-4">
              <FaCalendarAlt className="mr-2 text-blue-500" /> 習い事・通塾状況
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                {lessons.map(l => (
                  <div key={l.id} className="flex justify-between items-center p-2 bg-gray-50 rounded text-sm">
                    <span>{["日", "月", "火", "水", "木", "金", "土"][l.day_of_week]}：{l.lesson_name} <small className="text-gray-400">{l.remarks}</small></span>
                    {isAdmin && (
                      <button onClick={() => handleDeleteLesson(l.id)} className="text-gray-300 hover:text-red-500">
                        <FaTrashAlt size={12} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              {isAdmin && (
                <div className="p-3 border rounded-lg bg-gray-50 space-y-2">
                  <div className="flex gap-2">
                    <select className="text-xs border rounded p-1" value={lessonForm.day_of_week} onChange={e => setLessonForm({ ...lessonForm, day_of_week: parseInt(e.target.value) })}>
                      {[1, 2, 3, 4, 5, 6, 0].map(d => <option key={d} value={d}>{["日", "月", "火", "水", "木", "金", "土"][d]}</option>)}
                    </select>
                    <input className="text-xs border rounded p-1 flex-1" placeholder="習い事名" value={lessonForm.lesson_name} onChange={e => setLessonForm({ ...lessonForm, lesson_name: e.target.value })} />
                  </div>
                  <input className="text-xs border rounded p-1 w-full" placeholder="備考" value={lessonForm.remarks} onChange={e => setLessonForm({ ...lessonForm, remarks: e.target.value })} />
                  <button onClick={handleAddLesson} className="w-full bg-blue-600 text-white text-xs py-1.5 rounded">習い事を追加</button>
                </div>
              )}
            </div>
          </div>

          {/* 修正箇所2：余計な閉じカッコを削除し、正しくブロックを閉じる */}
        </div>
      )}

      {editModal.open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <h3 className="font-bold mb-4">{editModal.type === "record" ? "記録の編集" : "引継ぎの編集"}</h3>
            {editModal.type === "handover" && (
              <div className="grid grid-cols-2 gap-2 mb-4">
                <select className="border p-2 text-sm rounded" value={editModal.data.subjectid} onChange={e => setEditModal({ ...editModal, data: { ...editModal.data, subjectid: e.target.value } })}>
                  {subjects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                <select className="border p-2 text-sm rounded" value={editModal.data.teacherid} onChange={e => setEditModal({ ...editModal, data: { ...editModal.data, teacherid: e.target.value } })}>
                  {teachers.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
            )}
            <textarea
              className="w-full border p-2 text-sm rounded min-h-[100px]"
              value={editModal.data.content}
              onChange={e => setEditModal({ ...editModal, data: { ...editModal.data, content: e.target.value } })}
            />
            <div className="flex gap-2 mt-4">
              <button onClick={() => setEditModal({ open: false, type: "", data: null })} className="flex-1 py-2 border rounded">キャンセル</button>
              <button onClick={handleSaveEdit} className="flex-1 py-2 bg-blue-600 text-white rounded">保存</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default StudentKarte;